import React, { useState, useEffect } from 'react';
import { X, MapPin, User, Clock, FileText, Activity, Phone, Home, Briefcase } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { getAssignmentActivities } from '../../../models/mmsModel';
import { toast } from 'react-toastify';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with React 18
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

/**
 * Local Assignment Detail Modal
 * Shows full details of a locally assigned job with activities
 */
const LocalAssignmentDetailModal = ({ assignment: initialAssignment, onClose, onRefresh }) => {
  const [assignment, setAssignment] = useState(initialAssignment);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (activeTab === 'activities' && activities.length === 0) {
      fetchActivities();
    }
  }, [activeTab]);

  const fetchActivities = async () => {
    if (!assignment.assignment_id) {
      return;
    }

    try {
      setLoading(true);
      const response = await getAssignmentActivities(assignment.assignment_id);
      if (response && response.data && response.data.activities) {
        setActivities(response.data.activities);
      } else if (response && response.activities) {
        setActivities(response.activities);
      } else {
        setActivities([]);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      1: { label: 'Assigned', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      3: { label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      4: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
      5: { label: 'On Hold', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      6: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-300' }
    };
    
    const statusInfo = statusMap[status] || { 
      label: `Status ${status}`, 
      color: 'bg-gray-100 text-gray-800 border-gray-300' 
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Format assignment notes to replace 'Unknown User' with known worker name
  const formatAssignmentNotes = (notes) => {
    if (!notes) return notes;
    const workerName = assignment?.worker_name || assignment?.assigned_to_name;
    if (workerName && !/unknown user/i.test(workerName)) {
      return notes
        .replace(/\bJob\s+started\s+by\s+Unknown User\b/gi, `Job started by ${workerName}`)
        .replace(/\bby\s+Unknown User\b/gi, `by ${workerName}`);
    }
    return notes;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const latitude = assignment.LATITUDE || assignment.property_latitude;
  const longitude = assignment.LONGITUDE || assignment.property_longitude;
  const hasCoordinates = latitude && longitude;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 rounded-lg p-2" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Local Assignment - Job Card #{assignment.JOBCARD_NO || assignment.jobcard_no}</h2>
              {assignment.application_id && (
                <p className="text-xs text-gray-500">Application ID: {assignment.application_id}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-8">
            {['overview', 'activities', 'map'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Assignment Information</h3>
                {getStatusBadge(assignment.OM_STATUS || (assignment.assignment_status === 'completed' ? 6 : assignment.assignment_status === 'in-progress' ? 3 : 1))}
              </div>

              {/* Job Description - Show prominently - ONLY from EDAMS JOB_DESCRIPTION */}
              {(() => {
                // ONLY use JOB_DESCRIPTION from EDAMS - no fallbacks
                const description = assignment.JOB_DESCRIPTION;
                // Don't show if empty or if it's the workflow completion text
                if (!description || description.includes('Job completed via workflow')) {
                  return null;
                }
                return (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Job Description</h4>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {description}
                    </p>
                  </div>
                );
              })()}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Assigned To */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <User className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Assigned To</span>
                  </div>
                  <p className="text-sm text-gray-900">{assignment.ASSIGNED_TO_NAME || assignment.worker_name || 'Unknown Worker'}</p>
                  {assignment.worker_phone && (
                    <p className="text-xs text-gray-500 mt-1">{assignment.worker_phone}</p>
                  )}
                </div>

                {/* Assigned Date */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Clock className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Assigned Date</span>
                  </div>
                  <p className="text-sm text-gray-900">{formatDate(assignment.assigned_at || assignment.ISSUED_DATETIME)}</p>
                </div>

                {/* Address */}
                <div className="bg-gray-50 rounded-lg p-4 col-span-2">
                  <div className="flex items-center mb-2">
                    <Home className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Address</span>
                  </div>
                  <p className="text-sm text-gray-900">{assignment.ADDRESS || assignment.property_address || 'No address specified'}</p>
                </div>

                {/* Customer Info */}
                {assignment.CUSTOMER_NAME && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <User className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Customer</span>
                    </div>
                    <p className="text-sm text-gray-900">{assignment.CUSTOMER_NAME}</p>
                    {assignment.CELL_TEL_NO && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                        <Phone className="h-3 w-3 mr-1" />
                        {assignment.CELL_TEL_NO}
                      </p>
                    )}
                  </div>
                )}

                {/* Application ID */}
                {assignment.application_id && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Briefcase className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Application ID</span>
                    </div>
                    <p className="text-sm text-gray-900">#{assignment.application_id}</p>
                  </div>
                )}

                {/* Assignment Notes */}
                {(assignment.assignment_notes || assignment.notes) && (
                  <div className="bg-gray-50 rounded-lg p-4 col-span-2">
                    <div className="flex items-center mb-2">
                      <FileText className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Assignment Notes</span>
                    </div>
                    <p className="text-sm text-gray-900 italic whitespace-pre-line">
                      {formatAssignmentNotes(assignment.assignment_notes || assignment.notes)}
                    </p>
                  </div>
                )}

                {/* Status Timeline */}
                <div className="bg-gray-50 rounded-lg p-4 col-span-2">
                  <div className="flex items-center mb-2">
                    <Clock className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Status Timeline</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    {assignment.assigned_at && (
                      <p className="text-gray-700">
                        <span className="font-medium">Assigned:</span> {formatDate(assignment.assigned_at)}
                      </p>
                    )}
                    {assignment.started_at && (
                      <p className="text-gray-700">
                        <span className="font-medium">Started:</span> {formatDate(assignment.started_at)}
                      </p>
                    )}
                    {assignment.completed_at && (
                      <p className="text-gray-700">
                        <span className="font-medium">Completed:</span> {formatDate(assignment.completed_at)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Activity History</h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading activities...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No activities recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-r-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 capitalize">
                            {activity.activity_type?.replace('_', ' ') || 'Activity'}
                          </p>
                          {activity.description && (
                            <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                          )}
                          
                          {/* Display attachments if any */}
                          {activity.attachments && activity.attachments.length > 0 && (
                            <div className="mt-2 grid grid-cols-3 gap-2">
                              {activity.attachments.map((attachment, idx) => {
                                const uploadBaseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                                  ? 'http://silulumanzi.host' 
                                  : '';
                                const imageUrl = `${uploadBaseUrl}/uploads/mms/activities/${attachment.file_path}`;
                                
                                return (
                                  <div key={idx} className="relative">
                                    {attachment.is_image ? (
                                      <img 
                                        src={imageUrl}
                                        alt={attachment.file_name}
                                        className="w-full h-24 object-cover rounded border border-gray-200 cursor-pointer"
                                        onClick={() => window.open(imageUrl, '_blank')}
                                      />
                                    ) : (
                                      <a 
                                        href={imageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center w-full h-24 bg-gray-100 rounded border border-gray-200 text-xs text-gray-600 hover:bg-gray-200"
                                      >
                                        📎 {attachment.file_name}
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {activity.worker_name && `${activity.worker_name} • `}
                        {formatDate(activity.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Map Tab */}
          {activeTab === 'map' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Location Map</h3>
              
              {!hasCoordinates ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No coordinates available for this assignment</p>
                  {assignment.ADDRESS && (
                    <p className="text-sm text-gray-500 mt-2">Address: {assignment.ADDRESS}</p>
                  )}
                </div>
              ) : (
                <div className="h-96 rounded-lg overflow-hidden border border-gray-200" key={`map-${latitude}-${longitude}`}>
                  <MapContainer
                    key={`map-container-${latitude}-${longitude}`}
                    center={[parseFloat(latitude), parseFloat(longitude)]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                    whenReady={() => {
                      // Map is ready
                    }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker position={[parseFloat(latitude), parseFloat(longitude)]}>
                      <Popup>
                        <div>
                          <p className="font-medium">Job Card #{assignment.JOBCARD_NO || assignment.jobcard_no}</p>
                          {assignment.ADDRESS && <p className="text-sm">{assignment.ADDRESS}</p>}
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocalAssignmentDetailModal;

